import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { useDispatch } from "react-redux";
import apiClient from "../../../api/apiClient";
import { API_BASE_URL } from "../../../api/header";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { User, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import {
  setuser_data,
  setbearer_token,
  setuserpermission_data,
} from "../../../core/redux/action";
import { toast } from "sonner";

const Signin = () => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const route = all_routes;

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";

    if (savedRememberMe && savedUsername) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRememberMe(true);
      setCredentials((prev) => ({
        ...prev,
        username: savedUsername,
      }));
    }
  }, []);

  const loginMutation = useMutation({
    mutationFn: async (loginData) => {
      const res = await apiClient.post(`${API_BASE_URL}/login`, loginData);
      const responseData = res.data;
      try {
        const meRes = await apiClient.get(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${responseData.access_token}`,
          },
        });
        return {
          ...responseData,
          me: meRes.data.data,
        };
      } catch (meError) {
        console.error("Failed to fetch user profile after login:", meError);
        return responseData;
      }
    },
    onSuccess: (data) => {
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", credentials.username);
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberedUsername");
        localStorage.setItem("rememberMe", "false");
      }
      localStorage.removeItem("rememberedPassword");

      // Clear stale session ended flag to prevent immediate logout from useTabCloseLogout
      localStorage.removeItem("erf_session_ended");

      const userData = data.me || data.user;
      dispatch(setuser_data(userData));
      dispatch(setbearer_token(data.access_token));

      if (data.me?.permissions) {
        dispatch(setuserpermission_data(data.me.permissions));
      }

      toast.success("Login Successful");
      setTimeout(() => {
        navigate(route.dashboard);
      }, 1000);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || error.message || "Invalid credentials",
      );
    },
  });

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!credentials.username || !credentials.password) {
      return;
    }
    loginMutation.mutate(credentials);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="relative min-h-[100dvh] flex items-center justify-center p-4 md:p-6 lg:p-8 overflow-hidden bg-slate-50/30 dark:bg-transparent">
      {/* Soft atmospheric background blobs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-5%] left-[-5%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-5%] right-[-5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      <div className="w-full max-w-[420px] space-y-6 lg:space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
        {/* Modern Brand Identity */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="group relative">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-primary/30 to-primary/10 opacity-40 blur-md transition duration-500 group-hover:opacity-70" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl overflow-hidden bg-white dark:bg-card border border-border shadow-xs group-hover:scale-105 transition-transform shrink-0 p-1.5">
              <img
                src="/assets/img/logo-small.png"
                alt="Easy Retail Flow"
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <div className="flex flex-col items-center space-y-1">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-white drop-shadow-xs">
              Easy Retail Flow
            </h1>
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              Multi-Store POS
            </span>
          </div>
        </div>

        {/* Auth Card */}
        <Card className="border-none shadow-[0_20px_50px_rgba(0,0,0,0.1)] bg-card/70 backdrop-blur-xl ring-1 ring-border/50">
          <CardHeader className="space-y-1 pt-8 pb-6 text-center border-b border-border/40">
            <CardTitle className="text-2xl font-bold tracking-tight">
              Welcome back
            </CardTitle>
            <CardDescription className="text-muted-foreground/80 font-medium px-4">
              Sign in to manage your store's inventory, sales, and analytics
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8 px-8">
            <form onSubmit={handleSubmit} className="grid gap-6">
              <div className="grid gap-2.5">
                <Label
                  htmlFor="username"
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90 ml-1"
                >
                  Authorized Username
                </Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground/70" />
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Enter your username"
                    className="pl-11 h-12 bg-background/50 border-border/80 focus:border-primary/50 focus:ring-primary/20 transition-all rounded-xl"
                    value={credentials.username}
                    onChange={handleInputChange}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="grid gap-2.5">
                <div className="flex items-center justify-between ml-1">
                  <Label
                    htmlFor="password"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/90"
                  >
                    Security Password
                  </Label>
                  <Link
                    to={route.forgotPassword}
                    className="text-xs font-medium text-primary hover:underline underline-offset-4 transition-all"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground/70" />
                  <Input
                    id="password"
                    name="password"
                    type={isPasswordVisible ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-11 pr-11 h-12 bg-background/50 border-border/80 focus:border-primary/50 focus:ring-primary/20 transition-all rounded-xl"
                    value={credentials.password}
                    onChange={handleInputChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!isPasswordVisible)}
                    className="absolute right-3.5 top-3 text-muted-foreground/50 hover:text-primary transition-colors p-1 rounded-md"
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2 py-1 ml-1">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked)}
                  className="rounded-[4px] border-muted-foreground/30"
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-medium text-muted-foreground/80 cursor-pointer"
                >
                  Keep me signed in
                </Label>
              </div>

              <Button
                className="w-full h-12 text-sm font-bold tracking-wide shadow-xl shadow-primary/20 hover:scale-[1.01] transition-all rounded-xl mt-2"
                type="submit"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  "Sign In to Portal"
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col pt-2 pb-8 px-8">
            <div className="w-full border-t border-border/40 pt-6 px-4">
              <p className="text-[10px] text-center text-slate-500 dark:text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed">
                © {new Date().getFullYear()} Easy Retail Flow • Multi-Store POS
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Signin;
